import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSearch, IconX } from '@tabler/icons';

import {
  Container,
  StyledInput,
  ActionContainer,
  SearchButton,
  CloseButton,
} from './SearchBar.styles';

type SearchBarProps = {
  onClose: () => void;
  value?: string;
};

function SearchBar({
  onClose = () => {},
  value = '',
  ...rest
}: SearchBarProps) {
  const [term, setTerm] = useState(value);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  const onFocus = useCallback(() => setFocused(true), []);
  const onBlur = useCallback(() => setFocused(false), []);
  const handleSearch = useCallback(
    (event) => {
      setTerm(event.target.value);
    },
    [setTerm]
  );

  const gotoResults = useCallback(() => {
    navigate(`/search?q=${term}`);
    onClose();
  }, [navigate, term, onClose]);

  const handleKeyPress = useCallback(
    async (event) => {
      const char = event.which || event.keyCode;
      if (char === 13) {
        if (term.length <= 2) {
          return;
        }
        gotoResults();
      } else if (char === 27) {
        setTerm('');
        onClose();
      }
    },
    [term, gotoResults, onClose]
  );

  // If prop change we will react.
  useEffect(() => {
    setTerm(value || '');
  }, [value]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (focused) {
      document.addEventListener('keydown', handleKeyPress, false);
    }

    return () => document.removeEventListener('keydown', handleKeyPress, false);
  }, [focused, handleKeyPress]);

  const closeResults = useCallback(() => {
    setTerm('');
    onClose();
  }, [onClose]);

  return (
    <Container {...rest}>
      <StyledInput
        type="search"
        block
        value={term}
        onChange={handleSearch}
        placeholder="Search Something or Anything."
        onKeyPress={handleKeyPress}
        onFocus={onFocus}
        onBlur={onBlur}
        ref={inputRef}
        style={{
          boxSizing: 'border-box',
        }} // normalize.css sets to content-box
      />
      <ActionContainer>
        {term.length > 0 && (
          <CloseButton onClick={closeResults}>
            <IconX />
          </CloseButton>
        )}
        <SearchButton onClick={gotoResults} active={term.length > 0}>
          <IconSearch />
        </SearchButton>
      </ActionContainer>
    </Container>
  );
}

export default SearchBar;
